import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comicId = parseInt(id || '1', 10);

  const comic = useMemo(
    () => comics.find((c) => c.id === comicId),
    [comicId]
  );

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架。</p>
            <Link
              to="/"
              className="px-6 py-2 text-sm font-medium whitespace-nowrap rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === comic.category);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const sortedChapters = [...comic.chapters].sort((a, b) => b.number - a.number);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 pt-6 md:pt-8">
          <div className="w-full max-w-[1280px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category?.name || '分类', path: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="w-full max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              <div className="w-full lg:w-[320px] shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩漫免费阅读`}
                    title={`${comic.title} - ${comic.author} - 韩漫在线免费阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  {comic.title}
                </h1>

                <div className="flex items-center gap-4 mt-3 text-sm text-foreground-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {comic.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-400"></i>
                    {comic.rating}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {(comic.views / 10000).toFixed(0)}万阅读
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-medium whitespace-nowrap rounded-md ${
                    comic.status === 'ongoing' ? 'bg-primary-100 text-primary-700' : 'bg-foreground-200 text-foreground-700'
                  }`}>
                    {comic.status === 'ongoing' ? '连载中' : '已完结'}
                  </span>
                </div>

                <div className="mt-5">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="mt-5 flex items-center gap-3 flex-wrap">
                  <button className="px-6 py-2.5 text-sm font-semibold whitespace-nowrap rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200 cursor-pointer">
                    开始阅读 第1话
                  </button>
                  <button className="px-6 py-2.5 text-sm font-medium whitespace-nowrap rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer">
                    <i className="ri-bookmark-line mr-1"></i>加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto">
            <div className="flex items-end justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                章节列表
              </h2>
              <span className="text-sm text-foreground-500 whitespace-nowrap">
                共 {comic.chapters.length} 话
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {sortedChapters.map((ch) => (
                <button
                  key={ch.id}
                  className="text-left p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer group"
                >
                  <p className="text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">
                    {ch.title}
                  </p>
                  <p className="text-xs text-foreground-500 mt-1">{ch.updatedAt} 更新</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="w-full max-w-[1280px] mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  猜你喜欢
                </h2>
                <p className="mt-2 text-sm text-foreground-500">基于你的浏览偏好为你推荐</p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${comic.title} - 韩漫免费阅读`,
          description: comic.description,
          author: { '@type': 'Person', name: comic.author },
          datePublished: '2025-01-01',
          dateModified: comic.updatedAt,
          image: comic.coverImage,
          url: `http://ryjfh.cn/article/${comic.id}`,
          publisher: { '@type': 'Organization', name: '韩漫网' },
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://ryjfh.cn/' },
            { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `http://ryjfh.cn/category/${comic.category}` },
            { '@type': 'ListItem', position: 3, name: comic.title },
          ],
        })}
      </script>
    </>
  );
}